// @ts-nocheck
import React from "react";
import { useHistory } from "react-router";
import { isDeviceSocketLive } from "../../utils/deviceSocket";

interface BakeryProcessCanvasProps {
  deviceMetrics: Record<number, Record<string, any[]>>;
  devices: any[];
}

/**
 * Step-by-step production process view for the Dashboard "Process" tab.
 * Steps that have live devices (fermentor, Diosna mixer, QA camera) read real metrics;
 * the rest are placeholders/mocks until their hardware/telemetry lands (silos, bulk/proof
 * rise via ESP32, oven decks, robot arm).
 */
const BakeryProcessCanvas: React.FC<BakeryProcessCanvasProps> = ({ deviceMetrics, devices }) => {
  const history = useHistory();

  const getLatest = (deviceId: number, key: string, fallback: number | string = 0) => {
    const arr = deviceMetrics[deviceId]?.[key];
    if (!arr || arr.length === 0) return fallback;
    return arr[arr.length - 1];
  };

  const findDeviceByName = (pattern: string) =>
    devices?.find((d: any) => d.name?.toLowerCase().includes(pattern.toLowerCase()));

  const isOnline = (deviceId?: number) => {
    if (deviceId == null) return false;
    const d = devices?.find((dev: any) => dev.id === deviceId);
    return isDeviceSocketLive(d);
  };

  const goToDevice = (deviceId?: number) => {
    if (deviceId) history.push(`/devices/${deviceId}/general`);
  };

  // ── Live device wiring ───────────────────────────────────────────────
  const fermentorDevice = findDeviceByName("fermentor");
  const fermentorId = fermentorDevice?.id ?? 7;
  const fermentorOnline = isDeviceSocketLive(fermentorDevice);

  const diosnaDevice = findDeviceByName("diosna");
  const diosnaId = diosnaDevice?.id ?? 19;
  const diosnaOnline = isDeviceSocketLive(diosnaDevice);

  const qaOnline = isOnline(12);

  // ── STEP 1: Silos (hardcoded placeholders) ───────────────────────────
  const flourSilos = Array.from({ length: 8 }, (_, i) => ({
    name: `Flour ${i + 1}`,
    qty: "— kg",
  }));
  const otherSilos = [
    { name: "Salt", qty: "— kg" },
    { name: "Oil", qty: "— L" },
  ];

  // ── STEP 2: Fermentor (device 7, live) ───────────────────────────────
  const fermentor = [
    { label: "pH", value: `${Number(getLatest(fermentorId, "ph", 4.5)).toFixed(2)}` },
    { label: "Temp", value: `${Number(getLatest(fermentorId, "tt", 26.0)).toFixed(1)} °C` },
    { label: "Level", value: `${Number(getLatest(fermentorId, "lt", 320)).toFixed(0)} L` },
    { label: "CO₂", value: `${Number(getLatest(fermentorId, "co2", 0)).toFixed(1)} %` },
    { label: "DO", value: `${Number(getLatest(fermentorId, "do", 0)).toFixed(1)} mg/L` },
    { label: "Weight", value: `${Number(getLatest(fermentorId, "wt", 185)).toFixed(1)} kg` },
    { label: "V_ferm", value: `${Number(getLatest(fermentorId, "vferm", 0)).toFixed(1)} kg/h` },
  ];

  // ── STEP 3: Mixer (Diosna, live) ─────────────────────────────────────
  const currentDough = String(getLatest(diosnaId, "current_dough", getLatest(diosnaId, "recipe", "—")));
  const mixerState = String(getLatest(diosnaId, "state", "Idle"));
  const doughTemp = Number(getLatest(diosnaId, "dough_temperature", 0)).toFixed(1);

  // ── STEP 4/6: Bulk & Proof fermentation batches (ESP32 rise %, mocked) ─
  // Ready threshold: a batch is "ready to advance" at/above this rise %.
  const RISE_READY = 80;
  const bulkBatches = [
    { name: "Country", rise: 45 },
    { name: "Sourdough", rise: 82 },
    { name: "Rye", rise: 63 },
    { name: "Ciabatta", rise: 91 },
    { name: "Baguette", rise: 28 },
  ];
  const proofBatches = [
    { name: "Country", rise: 88 },
    { name: "Sourdough", rise: 55 },
    { name: "Rye", rise: 74 },
    { name: "Baguette", rise: 96 },
  ];

  // ── STEP 7: Oven (4 decks, hardcoded) ────────────────────────────────
  const ovenDecks = [
    { deck: 1, time: "1 / 24 min", temp: "240 °C" },
    { deck: 2, time: "3 / 24 min", temp: "235 °C" },
    { deck: 3, time: "—",          temp: "230 °C" },
    { deck: 4, time: "—",          temp: "225 °C" },
  ];

  const stepColor = "#53bde4";

  return (
    <div style={S.wrap}>
      <div style={S.grid}>
        {/* STEP 1 — Ingredients / Silos */}
        <StepCard n={1} title="Ingredients — Silos" color="#53bde4">
          <div style={S.siloGrid}>
            {flourSilos.map((s) => (
              <Silo key={s.name} name={s.name} qty={s.qty} accent="#c9a227" />
            ))}
            {otherSilos.map((s) => (
              <Silo key={s.name} name={s.name} qty={s.qty} accent="#6c9dd6" />
            ))}
          </div>
        </StepCard>

        {/* STEP 2 — Fermentor */}
        <StepCard
          n={2} title="Fermentor" color="#9c27b0"
          online={fermentorOnline} onClick={() => goToDevice(fermentorDevice?.id)}
        >
          <MetricRows rows={fermentor} />
        </StepCard>

        {/* STEP 3 — Mixer (Diosna) */}
        <StepCard
          n={3} title="Mixer — Diosna" color="#00ff88"
          online={diosnaOnline} onClick={() => goToDevice(diosnaId)}
        >
          <div style={S.doughBanner}>
            <span style={S.doughLabel}>Currently mixing</span>
            <span style={S.doughValue}>{currentDough}</span>
          </div>
          <MetricRows rows={[
            { label: "State", value: mixerState },
            { label: "Dough temp", value: `${doughTemp} °C` },
          ]} />
        </StepCard>

        {/* STEP 4 — Bulk fermentation */}
        <StepCard n={4} title="Bulk Fermentation — Chamber" color="#ff9800">
          <MetricRows rows={[
            { label: "Temp", value: "— °C" },
            { label: "Humidity", value: "— %" },
          ]} />
          <BatchList batches={bulkBatches} readyAt={RISE_READY} />
        </StepCard>

        {/* STEP 5 — Shaping (hardcoded) */}
        <StepCard n={5} title="Shaping" color="#ff9800">
          <MetricRows rows={[
            { label: "Pieces", value: "120 pcs" },
            { label: "Unit weight", value: "0.75 kg" },
            { label: "Line", value: "Auto" },
          ]} />
        </StepCard>

        {/* STEP 6 — Proof fermentation (copy of bulk) */}
        <StepCard n={6} title="Proof Fermentation — Chamber" color="#4caf50">
          <MetricRows rows={[
            { label: "Temp", value: "— °C" },
            { label: "Humidity", value: "— %" },
          ]} />
          <BatchList batches={proofBatches} readyAt={RISE_READY} />
        </StepCard>

        {/* STEP 7 — Oven */}
        <StepCard n={7} title="Oven — 4 Decks" color="#f44336">
          <div style={S.deckGrid}>
            {ovenDecks.map((d) => (
              <div key={d.deck} style={S.deck}>
                <div style={S.deckTitle}>Deck {d.deck}</div>
                <div style={S.deckRow}><span style={S.deckKey}>Time</span><span style={S.deckVal}>{d.time}</span></div>
                <div style={S.deckRow}><span style={S.deckKey}>Temp</span><span style={S.deckVal}>{d.temp}</span></div>
              </div>
            ))}
          </div>
        </StepCard>

        {/* STEP 8 — Robot arm + Camera QA */}
        <StepCard
          n={8} title="Robot Arm + Camera QA" color="#9c27b0"
          online={qaOnline} onClick={() => goToDevice(12)}
        >
          <MetricRows rows={[
            { label: "Robot arm", value: isOnline(24) ? "Active" : "Idle" },
            { label: "QA camera", value: qaOnline ? "Inspecting" : "Offline" },
          ]} />
          <div style={S.qaGrid}>
            <QaTile label="Volume" value="—" />
            <QaTile label="Coloration" value="—" />
            <QaTile label="Structure" value="—" />
          </div>
        </StepCard>
      </div>

      <div style={S.legend}>
        <Dot c="#00ff88" /> <span style={S.legendTxt}>Online</span>
        <Dot c="#ff3366" /> <span style={S.legendTxt}>Offline</span>
        <span style={{ ...S.legendTxt, marginLeft: 16 }}>● Rise ≥ {RISE_READY}% = ready to advance</span>
      </div>
    </div>
  );
};

// ── Presentational subcomponents ───────────────────────────────────────

const StepCard = ({ n, title, color, online, onClick, children }: any) => (
  <div
    style={{ ...cs.card, borderTop: `3px solid ${color}`, cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <div style={cs.header}>
      <span style={{ ...cs.badge, background: color }}>{n}</span>
      <span style={cs.title}>{title}</span>
      {online !== undefined && (
        <span style={{ ...cs.dot, background: online ? "#00ff88" : "#ff3366" }} />
      )}
    </div>
    <div style={cs.body}>{children}</div>
  </div>
);

const MetricRows = ({ rows }: any) => (
  <div>
    {rows.map((r: any) => (
      <div key={r.label} style={cs.row}>
        <span style={cs.key}>{r.label}</span>
        <span style={cs.val}>{r.value}</span>
      </div>
    ))}
  </div>
);

const Silo = ({ name, qty, accent }: any) => (
  <div style={cs.silo}>
    <div style={{ ...cs.siloBar, background: accent }} />
    <div style={cs.siloName}>{name}</div>
    <div style={cs.siloQty}>{qty}</div>
  </div>
);

const BatchList = ({ batches, readyAt }: any) => (
  <div style={cs.batchList}>
    {batches.map((b: any) => {
      const ready = b.rise >= readyAt;
      const c = ready ? "#00ff88" : "#ffb300";
      return (
        <div key={b.name} style={cs.batchRow}>
          <span style={{ ...cs.batchDot, background: c }} />
          <span style={cs.batchName}>{b.name}</span>
          <div style={cs.riseTrack}>
            <div style={{ ...cs.riseFill, width: `${Math.min(100, b.rise)}%`, background: c }} />
          </div>
          <span style={{ ...cs.batchRise, color: c }}>{b.rise}%</span>
        </div>
      );
    })}
  </div>
);

const QaTile = ({ label, value }: any) => (
  <div style={cs.qaTile}>
    <div style={cs.qaVal}>{value}</div>
    <div style={cs.qaLabel}>{label}</div>
  </div>
);

const Dot = ({ c }: any) => (
  <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: c, marginRight: 5 }} />
);

// ── Styles ─────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  wrap: { width: "100%", background: "#0d1117", borderRadius: 12, border: "1px solid rgba(0,212,255,0.2)", padding: "1rem" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "1rem" },
  siloGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 8 },
  doughBanner: { display: "flex", flexDirection: "column", background: "rgba(0,255,136,0.08)", border: "1px solid rgba(0,255,136,0.3)", borderRadius: 8, padding: "8px 12px", marginBottom: 10 },
  doughLabel: { fontSize: "0.7rem", color: "#7fdcae", textTransform: "uppercase", letterSpacing: "0.5px" },
  doughValue: { fontSize: "1.1rem", color: "#e6edf3", fontWeight: 700, fontFamily: "monospace" },
  deckGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  deck: { background: "#12171c", border: "1px solid #232a31", borderRadius: 8, padding: "8px 10px" },
  deckTitle: { color: "#f88", fontSize: "0.8rem", fontWeight: 700, marginBottom: 4, fontFamily: "monospace" },
  deckRow: { display: "flex", justifyContent: "space-between", fontSize: "0.78rem" },
  deckKey: { color: "#8a9199" },
  deckVal: { color: "#e6edf3", fontFamily: "monospace" },
  qaGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 10 },
  legend: { display: "flex", alignItems: "center", marginTop: 14, paddingTop: 10, borderTop: "1px solid #1c2229" },
  legendTxt: { color: "#8a9199", fontSize: "0.75rem", fontFamily: "monospace", marginRight: 14 },
};

const cs: Record<string, React.CSSProperties> = {
  card: { background: "#12161a", border: "1px solid #232a31", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" },
  header: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "1px solid #1c2229" },
  badge: { width: 22, height: 22, borderRadius: "50%", color: "#0d1117", fontWeight: 800, fontSize: "0.8rem", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace" },
  title: { color: "#e6edf3", fontWeight: 600, fontSize: "0.92rem", flex: 1 },
  dot: { width: 9, height: 9, borderRadius: "50%" },
  body: { padding: "12px 14px" },
  row: { display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: "0.84rem" },
  key: { color: "#8a9199" },
  val: { color: "#e6edf3", fontFamily: "monospace" },
  silo: { background: "#12171c", border: "1px solid #232a31", borderRadius: 6, padding: "6px", textAlign: "center", position: "relative" },
  siloBar: { height: 4, borderRadius: 2, marginBottom: 6, opacity: 0.8 },
  siloName: { color: "#c7d0d9", fontSize: "0.72rem", fontWeight: 600 },
  siloQty: { color: "#8a9199", fontSize: "0.7rem", fontFamily: "monospace", marginTop: 2 },
  batchList: { marginTop: 10, display: "flex", flexDirection: "column", gap: 6 },
  batchRow: { display: "flex", alignItems: "center", gap: 8 },
  batchDot: { width: 9, height: 9, borderRadius: "50%", flexShrink: 0 },
  batchName: { color: "#e6edf3", fontSize: "0.8rem", width: 78, flexShrink: 0 },
  riseTrack: { flex: 1, height: 6, background: "#1c2229", borderRadius: 3, overflow: "hidden" },
  riseFill: { height: "100%", borderRadius: 3 },
  batchRise: { fontSize: "0.78rem", fontFamily: "monospace", width: 42, textAlign: "right", flexShrink: 0 },
  qaTile: { background: "#12171c", border: "1px solid #232a31", borderRadius: 8, padding: "10px 6px", textAlign: "center" },
  qaVal: { color: "#e6edf3", fontSize: "1.1rem", fontWeight: 700, fontFamily: "monospace" },
  qaLabel: { color: "#8a9199", fontSize: "0.7rem", marginTop: 2 },
};

export default BakeryProcessCanvas;
